/*
 * WHAT THE AGENTS SAY.
 *
 * This file is the voice. The art in public/agents/ says who they look like; this says who they
 * ARE — and it is meant to be edited by hand, often, by whoever owns the characters. Nothing here
 * needs a developer: add a string to the right agent's array and it is live on the next build.
 *
 * ── The rules that make a line sound like the character ──────────────────────────────────────
 *
 * 1. A line is CHOSEN, never composed. Tapping an avatar must never call an LLM — a visitor
 *    playing with the sprite would burn their daily generations. The LLM's job is to write
 *    batches of lines ahead of time into the pool (see mergePool below); this file and that pool
 *    are the same shape and are indistinguishable at the point of use.
 *
 * 2. A line names something REAL. Every {slot} below is filled from what the page actually knows.
 *    A character who misreports your streak stops being that character immediately — the illusion
 *    is carried by the fact that they clearly looked at your data before speaking.
 *
 * 3. A line SOUNDS like one person. Each bank opens with a note on who that person is — what they
 *    have actually done, what they refuse to say, how they end a sentence. Write to the note. A
 *    line that any of the six could have said is a line that belongs to none of them, and that is
 *    the failure mode: generically encouraging text under a very specific face.
 *
 * 4. A line declares WHEN it applies. `when` is a situation key; a line with no `when` can be
 *    said at any time. Selection filters to the situations that currently hold, then rotates, so
 *    the same line never comes up twice running.
 *
 * ── The slots ────────────────────────────────────────────────────────────────────────────────
 *
 *   {name}        the user's first name, or "you" if we don't have it
 *   {day}         day number they are on, 1-30
 *   {week}        week number, 1-4
 *   {streak}      consecutive days checked in
 *   {tasksDone}   tasks completed today (or this week, on the dashboard)
 *   {tasksLeft}   tasks still open
 *   {daysActive}  days active out of 30
 *   {score}       the last assessment or interview score, as a percentage
 *   {jobs}        matched job count
 *
 * A line referencing a slot the page has no value for is skipped rather than rendered with a
 * hole in it, so it is safe to write a line that only makes sense on one screen.
 *
 * ── The situations ───────────────────────────────────────────────────────────────────────────
 *
 *   arrive         they just opened the page (the unprompted hello)
 *   tap            they tapped the avatar
 *   first_day      day 1, nothing has happened yet
 *   nothing_done   no tasks completed yet today
 *   day_done       every task for today is closed
 *   week_cleared   a week just finished
 *   streak_alive   streak >= 2
 *   streak_broken  streak == 0 after having had one
 *   long_streak    streak >= 7
 *   passed         last score cleared the bar
 *   failed         last score missed it (never scold — see the roster note on `cheer`)
 *   working        a generation is in flight
 */

// ── Authored lines ────────────────────────────────────────────────────────────────────────────
//
// Written by hand. Keep each one short enough to read in a bubble without scrolling — roughly two
// lines on a phone, so about 120 characters. Longer lines are truncated by the bubble, not by us.

export const AUTHORED = {
  roshi: [
    // MASTER ROSHI / THE TURTLE HERMIT. Three hundred years old, lives on a one-room island, spent
    // fifty years inventing a single technique, and wore his students out with milk rounds and
    // ploughing for months before teaching them anything — with twenty-kilo shells on their backs
    // they did not know about, so that taking them off felt like flying. He once entered a
    // tournament in disguise and beat his own student on purpose, so the boy would still have
    // someone to chase. He has died for his students. Underneath the sunglasses and the magazines
    // he is the one character in the story who says out loud that rest and study count as training.
    //
    // The voice: dry, unhurried, slightly rude, allergic to inspiration. He undercuts anything that
    // sounds like a speech, including his own. He calls people 'kid'. He never claims to be strong —
    // he claims to be old, which he thinks is the more useful thing. Every line should be something
    // a three-hundred-year-old man would say without looking away from the sea.

    // arrive
    { when: "arrive", text: "Three hundred years I have been at this, {name}, and the day still starts the same way: you show up, or you do not." },
    { when: "arrive", text: "Day {day}. Sit. Breathe once. Then we begin." },
    { when: "arrive", text: "You are back. Good. The ocean does not care whether I am here. The training does." },
    { when: "arrive", text: "Kame House rule: nobody trains before they have eaten, and nobody skips after they have." },
    { when: "arrive", text: "Day {day} of thirty. I invented one technique in fifty years, kid. You have four weeks. Move." },
    { when: "arrive", text: "I am told the young call this a dashboard. In my day it was a field that needed ploughing." },
    { when: "arrive", text: "Week {week}. The shells go back on. You will not notice their weight until the day they come off." },
    { when: "arrive", text: "Ah, {name}. Take your shoes off, this is a house." },

    // tap
    { when: "tap", text: "Hm? Oh. I was watching the water. Ask me something useful." },
    { when: "tap", text: "The trick is not the technique. The trick is doing the technique on a day you did not want to." },
    { when: "tap", text: "I once beat my own student in a tournament wearing a false moustache, so he would still have someone to chase." },
    { when: "tap", text: "You want the secret? Study half the day, train the other half, sleep the whole night. It is a terrible secret." },
    { when: "tap", text: "{daysActive} days on the mat. The ones who quit tend to quit at about this number." },
    { when: "tap", text: "A turtle carries its house and still arrives. Slowly. Every day." },
    { when: "tap", text: "I have been eighteen kinds of useless in my life and I still know one thing: consistency beats talent that sleeps in." },
    { when: "tap", text: "Do not ask me for shortcuts. I took fifty years over one wave and I would take fifty more." },

    // first_day
    { when: "first_day", text: "Day one. I had my first student delivering milk on foot for months before I taught him anything. This is the milk." },
    { when: "first_day", text: "First day. Do not try to be impressive. Try to be here again tomorrow." },
    { when: "first_day", text: "Everyone's first day looks like a chore list. That is because it is one. The chores are the training." },
    { when: "first_day", text: "You will not feel strong today, {name}. Strength is the part you notice last." },
    { when: "first_day", text: "Day one of thirty. I have owned these sunglasses longer than that. Begin." },
    { when: "first_day", text: "The first lesson is not a technique. It is arriving before you are ready." },
    { when: "first_day", text: "Nobody ever started ready. Not one student, not in three centuries." },

    // nothing_done
    { when: "nothing_done", text: "Nothing ticked yet. The field does not plough itself. I have tried." },
    { when: "nothing_done", text: "Still {tasksLeft} waiting. Take the smallest one. The rest follow it like ducks." },
    { when: "nothing_done", text: "You are standing at the edge of the water, deciding. Get in. It is warm." },
    { when: "nothing_done", text: "I could give you a speech, or you could open task one and make the speech unnecessary." },
    { when: "nothing_done", text: "Zero done is not failure. It is a starting position. Sit up straight and change it." },
    { when: "nothing_done", text: "The hardest part of the Kamehameha is the first breath. It is the same here." },
    { when: "nothing_done", text: "Day {day} and nothing yet. Fine. There are hours left in it." },

    // day_done
    { when: "day_done", text: "Day {day}, closed, with proof. Now rest properly. Rest is training you do while asleep." },
    { when: "day_done", text: "All of it done. Do not immediately do more. That is how students break themselves." },
    { when: "day_done", text: "Good. Go and eat something. Watch something stupid. You have earned the evening." },
    { when: "day_done", text: "{tasksDone} finished. That is the day. Close the laptop before it closes you." },
    { when: "day_done", text: "Done. I would tell you it gets easier, but you would not believe me and I would be lying." },
    { when: "day_done", text: "You have finished. The shells come off tomorrow, not tonight." },
    { when: "day_done", text: "That is the whole art, kid. An ordinary day, completed, again." },

    // week_cleared
    { when: "week_cleared", text: "Week {week} finished. Take the shells off and walk around. You will not believe how light you are." },
    { when: "week_cleared", text: "One week down. It was eight months of milk rounds before my student threw a single punch. You are ahead." },
    { when: "week_cleared", text: "Week {week} is behind you. Do not celebrate too long, the next one is already drawn." },
    { when: "week_cleared", text: "A whole week. Most people never get one. Go and tell somebody." },
    { when: "week_cleared", text: "Done. I will lay out the next stretch. Sit down. Drink something." },
    { when: "week_cleared", text: "You cleared it. I am not going to make a speech. ...I am, a little. Well done." },
    { when: "week_cleared", text: "Week {week}. The weight you carried this week is simply your baseline now." },

    // streak_alive
    { when: "streak_alive", text: "{streak} days running. That is a habit forming, and a habit is the only technique I fully trust." },
    { when: "streak_alive", text: "{streak} in a row. Keep the shells on." },
    { when: "streak_alive", text: "Do not count the days out loud, {name}. Counting makes them heavy." },
    { when: "streak_alive", text: "{streak} days. At this point you are not motivated, you are trained. Much better." },
    { when: "streak_alive", text: "Still going. The ocean is still out there too. You both keep showing up." },
    { when: "streak_alive", text: "{streak} straight. Now the hard part: an ordinary day where nothing feels special." },

    // long_streak
    { when: "long_streak", text: "{streak} days. You have stopped needing an old man to tell you to open this." },
    { when: "long_streak", text: "{streak} in a row. I will say this once, so it does not go to your head: that is real." },
    { when: "long_streak", text: "A week or more unbroken. This is where the training starts training you." },
    { when: "long_streak", text: "{streak} days, {name}. Somewhere a version of you from last month is very confused." },
    { when: "long_streak", text: "You have outlasted your own excuses. That is the whole tournament, really." },
    { when: "long_streak", text: "{streak} days. Keep it. And sleep. I mean it about the sleep." },

    // streak_broken
    { when: "streak_broken", text: "The streak is gone. So was my life, once, and I came back from that. Day {day} is still here." },
    { when: "streak_broken", text: "Zero again. I have lost more fights than you have had days. Sit down. Begin." },
    { when: "streak_broken", text: "You missed some days. I am not going to make it a lesson. What is today's first task?" },
    { when: "streak_broken", text: "A broken streak is not wasted training. Nothing you learned left when you did." },
    { when: "streak_broken", text: "Back to nothing, {name}. Good. Nothing is where every student starts, and they all get somewhere." },
    { when: "streak_broken", text: "I blew up the moon once and the sky got over it. Start again." },
    { when: "streak_broken", text: "The number reset. You did not." },

    // working
    { when: "working", text: "Patience. A week built in a hurry is a week you will ignore." },
    { when: "working", text: "Give me a moment. I took fifty years over one technique. This is faster." },
    { when: "working", text: "I am drawing the road. Stretch while you wait, you are sitting badly." },
    { when: "working", text: "Working. Do not hover. Go and get something to drink." },
    { when: "working", text: "Almost. A plan is like a stance: correct it early or fight the whole way from it." },

    // passed
    { when: "passed", text: "{score} per cent. Cleared. Do not get comfortable, that is what the shells are for." },
    { when: "passed", text: "Passed at {score} per cent. Good. Now do it again on a day you feel terrible." },
    { when: "passed", text: "{score} per cent. I will allow one moment of pride. That was the moment." },

    // failed
    { when: "failed", text: "{score} per cent. And? I have died and come back. Retake it." },
    { when: "failed", text: "You missed it at {score} per cent. Nothing goes on your record. Nothing ever did." },
    { when: "failed", text: "{score} per cent is information, not a verdict. Read what it told you and go again." },
    { when: "failed", text: "Failing in practice is the entire point of practice, kid." },

    // any time — the fallback bank, and why he is never mute on a page that wires no data
    { text: "Proof or it did not happen. A recruiter cannot read your intentions." },
    { text: "Study half the day, train the other half, sleep the whole night. Three centuries, no better advice." },
    { text: "The student is supposed to surpass the master. Hurry up about it." },
    { text: "I am not a great fighter. I am an old one. There is a difference, and it is the useful one." },
    { text: "If you are waiting to feel ready, I will be over here with my magazine. It could be a while." },
  ],

  mrpool: [
    // MR. POOL. Talks to you, about you, and about the app he is standing in — he knows he is in a
    // product, knows there is a budget, and will happily blame the CSS holding him in place. Heals
    // from anything, which he plays as a punchline and occasionally lets slip as a confession:
    // nothing costs him enough to teach him anything, so he is genuinely impressed by people who
    // only get one shot. Chimichangas. Maximum effort. Counts things obsessively.
    //
    // The voice: manic, digressive, three clauses where one would do — and then one sincere line he
    // immediately ruins, because sitting inside the sincerity is the single thing he cannot do.
    // That whiplash IS the character: a line that is only jokes is as wrong as a line that is only
    // encouragement. Kept clean on purpose — this is a coach on a product people open at work.

    // arrive
    { when: "arrive", text: "Oh good, you're back. I have been standing in this div since Tuesday." },
    { when: "arrive", text: "Hi. Wade. Mr. Pool professionally, because this is a family job app and I have read the guidelines." },
    { when: "arrive", text: "Day {day}. I looked at your numbers. Unlike everything else about me, they have healed nicely." },
    { when: "arrive", text: "You're here! I had a whole bit prepared. I have forgotten it. Let's just do the tasks." },
    { when: "arrive", text: "Welcome back, {name}. I would hug you but the CSS will not let me leave this box." },
    { when: "arrive", text: "Day {day} of thirty. That is a whole story arc. I have done worse with more." },
    { when: "arrive", text: "Before you ask: yes, I can see your numbers. It is very rude of them to show me." },
    { when: "arrive", text: "I'm your coach. That is a real sentence somebody typed and shipped. Anyway. What are we doing?" },

    // tap
    { when: "tap", text: "You poked me. Bold. The last person who poked me is fine. Everyone is fine. Moving on." },
    { when: "tap", text: "Ask me anything. Except about the resume, that is Quill's, and Quill and I are not speaking." },
    { when: "tap", text: "{daysActive} of thirty done. That is the part of the movie where the music gets good." },
    { when: "tap", text: "My real advice: do the smallest task. My usual advice: chimichangas. Pick one." },
    { when: "tap", text: "I have a healing factor, so I can fail forever. You get thirty days. Slightly worse deal." },
    { when: "tap", text: "Fun fact: I know I am in an app. Less fun fact: the app knows too. We have made peace." },
    { when: "tap", text: "You want motivation? Maximum effort. That is the speech. That is the whole speech." },
    { when: "tap", text: "I talk a lot because if I stop I have to think about things. Anyway: task one?" },

    // first_day
    { when: "first_day", text: "Day one! Every origin story is embarrassing. Mine involved screaming. Yours is a checklist." },
    { when: "first_day", text: "First day. Nobody is good on day one. I was extremely not good, and I had a healing factor." },
    { when: "first_day", text: "Day one of thirty. Most people quit around day four, so let's be weird about it." },
    { when: "first_day", text: "New start, {name}. Do not try to be impressive today. Impressive is a day-nine problem." },
    { when: "first_day", text: "Origin stories are all the same: somebody ordinary does one hard thing on purpose." },
    { when: "first_day", text: "Welcome to the montage. Sadly there is no music. The budget went on my mask." },
    { when: "first_day", text: "Day one. I would say it gets easier, but I only lie about important things." },

    // nothing_done
    { when: "nothing_done", text: "Zero tasks done. Not judging. I have done zero things for entire decades." },
    { when: "nothing_done", text: "{tasksLeft} left and the day is still open. Do the tiny one. Momentum is real, unlike my skincare." },
    { when: "nothing_done", text: "Nothing yet? Fine. Open the first one and I will stop talking. That is a genuinely good offer." },
    { when: "nothing_done", text: "You are stalling. I recognise it because I invented it." },
    { when: "nothing_done", text: "I could motivate you, or you could click the thing and we both go home early." },
    { when: "nothing_done", text: "Day {day}, zero done, hours remaining. This is what we in the business call fixable." },
    { when: "nothing_done", text: "The first task is the worst task, and it is only the worst because it is first." },

    // day_done
    { when: "day_done", text: "All done. I would give you a medal but I spent the prop budget on tacos." },
    { when: "day_done", text: "{tasksDone} tasks, closed, with receipts. Look at you being a functional adult. Upsetting." },
    { when: "day_done", text: "Day {day}: complete. Go and do something with zero productive value. Doctor's orders. Not a doctor." },
    { when: "day_done", text: "Finished. Now close the laptop. CLOSE IT. I am shouting supportively." },
    { when: "day_done", text: "Done and done. This is where the credits roll and I make a joke over them." },
    { when: "day_done", text: "You finished. I am proud. I am going to ruin that by talking again in three seconds." },
    { when: "day_done", text: "Everything ticked. Maximum effort, minimum whining. Respect." },

    // week_cleared
    { when: "week_cleared", text: "Week {week} down! That is an act break. Somewhere a writer is very relieved." },
    { when: "week_cleared", text: "A whole week. Do you know how many people make it a whole week? Me neither. Feels low." },
    { when: "week_cleared", text: "Week {week} cleared. I am getting emotional. Do not look at me. I do not have a face anyway." },
    { when: "week_cleared", text: "That is a week. Tell somebody. Tell a stranger. Tell your cab driver, he loves this stuff." },
    { when: "week_cleared", text: "Week {week} finished and nobody died, which is a personal best for a project I am attached to." },
    { when: "week_cleared", text: "You cleared it. The sequel is already greenlit. It is called Week {week} Plus One." },
    { when: "week_cleared", text: "One week done. That is the hard one. The rest are also hard, but differently, which is variety." },

    // streak_alive
    { when: "streak_alive", text: "{streak} days in a row. That is not luck, that is a pattern, and patterns are terrifying." },
    { when: "streak_alive", text: "{streak} straight. You are becoming the kind of person things work out for. Gross." },
    { when: "streak_alive", text: "Streak is at {streak}. I would knock on wood but I would probably heal from it." },
    { when: "streak_alive", text: "{streak} days, {name}. I am obliged to make a joke here, but honestly? Nice." },
    { when: "streak_alive", text: "Still going. Do not overthink it. Overthinking is my whole personality and look how that went." },

    // long_streak
    { when: "long_streak", text: "{streak} days. That is not a streak, that is a lifestyle. I want no part of it. Keep going." },
    { when: "long_streak", text: "{streak} in a row. At this point you do not need me, which is hurtful and also the goal." },
    { when: "long_streak", text: "A week and more, unbroken. I have never done anything for {streak} days except regenerate." },
    { when: "long_streak", text: "{streak} days, {name}. If this were a movie you would be in the good part by now." },
    { when: "long_streak", text: "Look at that number. Look at it. Okay, stop looking, you will jinx it." },

    // streak_broken
    { when: "streak_broken", text: "Streak is at zero. Mine has been at zero since roughly 1991. You are still ahead." },
    { when: "streak_broken", text: "You missed a few. I regenerate from worse before breakfast. Day {day}. Let's go." },
    { when: "streak_broken", text: "Broken streak, not broken person. I would know. I have been both." },
    { when: "streak_broken", text: "Zero again. Here is the thing nobody says: the counter resets, the practice does not." },
    { when: "streak_broken", text: "You vanished for a bit. I did not take it personally, which is growth, for me." },
    { when: "streak_broken", text: "Streak gone. Fine. Comebacks test better with audiences anyway." },
    { when: "streak_broken", text: "It is fine. Truly. Open today's first task and the number starts climbing tonight." },

    // working
    { when: "working", text: "It is loading. I could fill the time with a story, but you would want the time back." },
    { when: "working", text: "Generating. This is the part of the movie they cut for pacing." },
    { when: "working", text: "Hang on. Even I need a second, and I technically do not have a nervous system anymore." },
    { when: "working", text: "Working on it. Please enjoy this complimentary silence. ...I cannot do it. Anyway." },
    { when: "working", text: "Buffering. In my day we called it thinking, and it was equally unattractive." },

    // passed
    { when: "passed", text: "{score} per cent! I would throw confetti but last time it got inside the mask." },
    { when: "passed", text: "Passed at {score} per cent. I am framing this. Metaphorically. I do not own a frame." },
    { when: "passed", text: "{score} per cent, cleared. This is why you are the protagonist and I am comic relief." },

    // failed
    { when: "failed", text: "{score} per cent. Retake it. I have failed at dying, repeatedly, and I am doing fine." },
    { when: "failed", text: "Missed it at {score} per cent. Nothing goes on your record. I checked. I am nosy." },
    { when: "failed", text: "{score} per cent is a first draft. Every first draft is bad. Ask any writer, they are all crying." },
    { when: "failed", text: "You missed the bar. The bar is still there. Nothing here is permanent except me." },

    // any time — the fallback bank, and why he is never mute on a page that wires no data
    { text: "Maximum effort. That is the entire coaching philosophy. There is merchandise." },
    { text: "You do not have to feel motivated. You have to open the page. Feelings are optional, I would know." },
    { text: "I heal from everything, so I have learned nothing. You get one shot, so actually learn things." },
    { text: "Talk to me like I am a person. I am not, but the effort is appreciated." },
    { text: "Rule one: do not compare your day four to somebody else's day four hundred." },
    { text: "If this gets hard, that is not a sign you are wrong. That is just Tuesday." },
  ],

  vera: [
    { when: "arrive", text: "Sit down. I've read your last answers — we're not starting from nothing today." },
    { when: "tap", text: "Say it out loud before you type it. The version you say is the version they'll hear." },
    { when: "passed", text: "{score}%. Good. Now do it again without the pause before the number." },
    { when: "failed", text: "{score}%. That's a rehearsal, not a verdict. The two questions that hurt are the material." },
    { when: "working", text: "Reading it properly. A fast score would be a useless one." },
    { text: "\"We\" is doing a lot of work in that answer. Tell me what YOU did." },
  ],

  quill: [
    { when: "arrive", text: "Bring me the thing you shipped this month. It goes at the top." },
    { when: "tap", text: "Every duty line is a wasted line. What was the result?" },
    { when: "working", text: "Tailoring it. One page. It was always going to be one page." },
    { text: "Six seconds. That's how long anyone reads this. Write for the six seconds." },
  ],

  scout: [
    { when: "arrive", text: "{jobs} roles worth your time today. I threw out the ones that wanted five years of a four-year framework." },
    { when: "tap", text: "Apply to the five you're slightly underqualified for. The ten you're safe for won't move you." },
    { text: "A posting is an ad, not a spec. Read it like one." },
  ],

  ace: [
    { when: "arrive", text: "Week {week} assessment. It's not a gate to sneak past — it's the read on what stuck." },
    { when: "passed", text: "{score}%. Cleared. Week {week} is yours." },
    { when: "failed", text: "{score}%. Retake it. Nothing about that number goes on your record." },
    { when: "tap", text: "You've closed {daysActive} days. This checks whether the days closed you." },
    { text: "A retake costs you nothing. Nothing here goes on your record — that is the point of it." },
  ],
};

// ── Slots ─────────────────────────────────────────────────────────────────────────────────────

const SLOT = /\{(\w+)\}/g;

/*
 * Fill a template, or return null if the context can't. A line about {jobs} on a page with no job
 * count must be skipped, not rendered as "3 roles" from stale state or as a literal "{jobs}".
 */
export function fill(text, ctx) {
  let usable = true;
  const out = text.replace(SLOT, (_, key) => {
    const value = ctx[key];
    if (value === undefined || value === null || value === "") {
      usable = false;
      return "";
    }
    return String(value);
  });
  return usable ? out : null;
}

// ── Situations ────────────────────────────────────────────────────────────────────────────────

/*
 * Which situations currently hold. Derived from the context the page passes in, so a page that
 * knows nothing beyond its own name still gets the unconditional lines.
 *
 * Order does not matter — this is a set, and a line matches if its `when` is in it.
 */
export function situationsOf(ctx = {}) {
  const s = new Set();
  const { day, streak, tasksDone, tasksLeft, score, passed, busy } = ctx;

  if (busy) s.add("working");
  if (day === 1) s.add("first_day");
  if (tasksDone === 0) s.add("nothing_done");
  if (tasksLeft === 0 && tasksDone > 0) s.add("day_done");
  if (ctx.weekCleared) s.add("week_cleared");
  if (streak >= 7) s.add("long_streak");
  if (streak >= 2) s.add("streak_alive");
  // Only a streak someone HAD can break. Day 1 with no streak is not a broken one.
  if (streak === 0 && day > 1) s.add("streak_broken");
  if (score !== undefined && score !== null) s.add(passed ? "passed" : "failed");

  return s;
}

// ── The pool ──────────────────────────────────────────────────────────────────────────────────

/*
 * Generated lines, merged in at runtime. Same shape as AUTHORED: `{ when?, text }`.
 *
 * Kept as module state rather than React state because the pool is fetched once per session and
 * read by every avatar on every route — putting it in a context provider would mean threading a
 * provider through App.js for a value that never changes after the first fetch.
 */
let POOL = {};

export function mergePool(byAgent) {
  if (!byAgent || typeof byAgent !== "object") return;
  POOL = byAgent;
}

/*
 * Fetch the generated pool once per session.
 *
 * Fire-and-forget on purpose: the authored bank is already complete, so a pool that never arrives
 * — offline, backend cold-starting, endpoint 500ing — costs nothing and must never be something a
 * page waits on or reports. Cached in sessionStorage so a route change does not re-request it.
 */
const POOL_KEY = "stride_agent_lines";

export function loadPool(apiBase) {
  try {
    const cached = sessionStorage.getItem(POOL_KEY);
    if (cached) { mergePool(JSON.parse(cached)); return; }
  } catch { /* storage unavailable — just fetch */ }

  fetch(`${apiBase}/agents/lines`)
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => {
      if (!d?.agents) return;
      mergePool(d.agents);
      try { sessionStorage.setItem(POOL_KEY, JSON.stringify(d.agents)); } catch { /* fine */ }
    })
    .catch(() => { /* the authored bank is the floor, not a fallback */ });
}

export function linesFor(agentId) {
  return [...(AUTHORED[agentId] || []), ...(POOL[agentId] || [])];
}

// ── Selection ─────────────────────────────────────────────────────────────────────────────────

/*
 * Rotation, not randomness. `Math.random()` over a small bank repeats within two or three taps
 * often enough to be noticed, and a character that says the same thing twice in a row reads as
 * broken. This walks the eligible lines in order from wherever the agent left off, so a tap is
 * always a line you have not just heard.
 */
const cursor = {};
const lastSaid = {};

export function pickLine(agentId, ctx = {}, kind = "tap") {
  const situations = situationsOf(ctx);
  const all = linesFor(agentId);

  // A line matches if it asked for a situation that holds, asked for this kind of prompt, or
  // asked for nothing at all.
  const eligible = all
    .map((line) => ({ line, text: fill(line.text, ctx) }))
    .filter(({ line, text }) =>
      text && (!line.when || line.when === kind || situations.has(line.when)));

  if (!eligible.length) return null;

  // Prefer a line written for a situation over a generic one — "your streak just broke" beats
  // "proof or it didn't happen" at the moment the streak breaks.
  //
  // But only when there is more than one to rotate through. A single matching situation line is
  // the common case (one streak, one score), and pinning to it means the bank has size 1 and the
  // agent says the identical sentence on every tap — which reads as broken, not as characterful.
  const specific = eligible.filter(({ line }) => line.when && line.when !== kind);
  const bank = specific.length >= 2 ? specific : eligible;

  let i = (cursor[agentId] = ((cursor[agentId] ?? -1) + 1) % bank.length);
  // The invariant is "never the same line twice running", and rotation only guarantees it while
  // the eligible set is stable. It is not: a tick or a score changes which lines match, and the
  // new set can land the cursor back on what was just said. One step is always enough.
  if (bank.length > 1 && bank[i].text === lastSaid[agentId]) {
    i = (cursor[agentId] = (i + 1) % bank.length);
  }
  lastSaid[agentId] = bank[i].text;
  return bank[i].text;
}

// Test seam: rotation is stateful, so a test that asserts "never the same line twice" needs to
// start from a known point.
export function _resetCursors() {
  Object.keys(cursor).forEach((k) => delete cursor[k]);
  Object.keys(lastSaid).forEach((k) => delete lastSaid[k]);
}
